import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { MagnifyingGlassIcon, FunnelIcon, ArrowPathIcon, Squares2X2Icon, ListBulletIcon, XMarkIcon, TagIcon, FolderPlusIcon, TrashIcon, BarsArrowDownIcon, ArrowsUpDownIcon, FolderIcon } from '@heroicons/react/24/outline';
import BookmarkCard from '../components/BookmarkCard';
import BookmarkListRow from '../components/BookmarkListRow';
import TagsManager from '../components/TagsManager';
import ErrorMessage from '../components/ErrorMessage';
import AddToCollectionModal from '../components/AddToCollectionModal';
import { bookmarkService } from '../services/bookmarkService';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

const PAGE_SIZE = 50;

// URL-based video detection — fallback for bookmarks that predate the mediaType field.
// Mirrors the server-side detectMediaType logic in extractMetadata.js.
function isVideoUrl(url) {
  if (!url) return false;
  try {
    const { hostname, pathname } = new URL(url);
    if (['youtube.com', 'www.youtube.com', 'm.youtube.com'].includes(hostname))
      return /^\/(watch|shorts|live|embed)/.test(pathname);
    if (hostname === 'youtu.be') return true;
    if (['vimeo.com', 'player.vimeo.com'].includes(hostname)) return /^\/\d+/.test(pathname);
    if (['ted.com', 'www.ted.com'].includes(hostname)) return pathname.startsWith('/talks/');
    return [
      'twitch.tv', 'www.twitch.tv',
      'loom.com', 'www.loom.com',
      'tiktok.com', 'www.tiktok.com',
      'dailymotion.com', 'www.dailymotion.com',
      'rumble.com', 'www.rumble.com',
      'wistia.com', 'fast.wistia.net',
    ].includes(hostname);
  } catch { return false; }
}

const BookmarkList = () => {
  const location = useLocation();
  const [bookmarks, setBookmarks] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filteredBookmarks, setFilteredBookmarks] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('bookmarkViewMode') || 'grid');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('bookmarkSortBy') || 'newest');
  const [highlightId, setHighlightId] = useState(location.state?.highlightId || null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(location.state?.filterStatus || 'all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [loadingCollectionId, setLoadingCollectionId] = useState(null);
  const collectionBookmarkIdsRef = useRef(new Map()); // collectionId → Set<bookmarkId>
  const listContainerRef = useRef(null);
  const [error, setError] = useState(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const intervalRef = useRef(null);

  // ── Bulk selection ────────────────────────────────────────────────────────
  const [readLaterOnly, setReadLaterOnly] = useState(location.state?.readLaterOnly || false);
  const [videoOnly, setVideoOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTagExpanded, setBulkTagExpanded] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [bulkTagFeedback, setBulkTagFeedback] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkCollectionOpen, setBulkCollectionOpen] = useState(false);

  const selectionActive = selectedIds.size > 0;

  // Unique tags from loaded bookmarks — used for datalist autocomplete
  const allTags = useMemo(() => {
    const tags = new Set();
    bookmarks.forEach(b => b.tags?.forEach(t => tags.add(t)));
    return [...tags].sort();
  }, [bookmarks]);

  const loadBookmarks = useCallback(async (options = {}) => {
    // `token` is passed explicitly by the Load more button so this function
    // doesn't need to close over nextToken state, keeping the dependency array empty.
    const { silent = false, loadMore = false, token = null } = options;
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else if (!silent) {
        setLoading(true);
        setError(null);
      } else {
        setRefreshing(true);
      }

      const response = await bookmarkService.getBookmarks({
        limit: PAGE_SIZE,
        nextToken: loadMore ? token : null,
      });
      const items = response.items || [];

      if (loadMore) {
        setBookmarks(prev => [...prev, ...items]);
        setNextToken(response.nextToken || null);
      } else if (silent) {
        // Merge first-page refresh: update changed items, prepend newly-added ones.
        // Don't reset nextToken — the user may have already loaded multiple pages.
        setBookmarks(prev => {
          const prevIds = new Set(prev.map(b => b.bookmarkId));
          const newOnes = items.filter(f => !prevIds.has(f.bookmarkId));
          const updated = prev.map(b => {
            const fresh = items.find(f => f.bookmarkId === b.bookmarkId);
            return fresh || b;
          });
          return [...newOnes, ...updated];
        });
      } else {
        setBookmarks(items);
        setNextToken(response.nextToken || null);
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
      // Don't show an error banner for auth failures during background polling —
      // App.js visibilitychange / tokenRefresh_failure handlers will redirect to
      // login when the user returns to the tab.
      const isAuthError = error.message === 'Authentication required';
      if (!silent || !isAuthError) {
        setError(error.message || 'Failed to load bookmarks');
      }
    } finally {
      if (loadMore) {
        setLoadingMore(false);
      } else if (!silent) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    loadBookmarks();
    bookmarkService.listCollections()
      .then(({ items }) => setCollections(items || []))
      .catch(() => {}); // collections filter is non-critical
    intervalRef.current = setInterval(() => {
      loadBookmarks({ silent: true });
    }, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadBookmarks]);

  useEffect(() => {
    if (location.state?.filterTag) {
      setSelectedTags([location.state.filterTag]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!highlightId || loading) return;
    const el = document.getElementById(`bookmark-${highlightId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setHighlightId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightId, loading]);

  // Lock body scroll when overlay drawer is open on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isFilterPanelOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFilterPanelOpen]);

  const availableCategories = useMemo(() => {
    const cats = new Set();
    bookmarks.forEach(b => b.categories?.forEach(c => cats.add(c)));
    return [...cats].sort();
  }, [bookmarks]);

  const filterBookmarks = useCallback(async () => {
    try {
      setSearching(true);
      setError(null);
      let results;

      if (searchTerm.trim()) {
        const searchResponse = await bookmarkService.searchBookmarks(searchTerm, {
          limit: 100,
          tags: selectedTags.length > 0 ? selectedTags : undefined
        });
        results = searchResponse.items || [];
      } else {
        results = bookmarks;
      }

      let filtered = results;

      if (!searchTerm.trim() && selectedTags.length > 0) {
        filtered = filtered.filter(bookmark =>
          bookmark.tags?.some(tag => selectedTags.includes(tag))
        );
      }

      if (statusFilter !== 'all') {
        filtered = filtered.filter(bookmark => bookmark.status === statusFilter);
      }

      if (selectedCategories.length > 0) {
        filtered = filtered.filter(bookmark =>
          bookmark.categories?.some(cat => selectedCategories.includes(cat))
        );
      }

      if (readLaterOnly) {
        filtered = filtered.filter(bookmark => bookmark.readLater === true);
      }

      if (videoOnly) {
        filtered = filtered.filter(bookmark =>
          bookmark.mediaType === 'video' || isVideoUrl(bookmark.url)
        );
      }

      if (selectedCollections.length > 0) {
        const allowed = new Set();
        selectedCollections.forEach(id => {
          collectionBookmarkIdsRef.current.get(id)?.forEach(bid => allowed.add(bid));
        });
        filtered = filtered.filter(b => allowed.has(b.bookmarkId));
      }

      setFilteredBookmarks(filtered);
    } catch (error) {
      console.error('Error filtering bookmarks:', error);
      setError(error.message || 'Search failed');

      let filtered = bookmarks;

      if (searchTerm.trim()) {
        filtered = filtered.filter(bookmark =>
          bookmark.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bookmark.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bookmark.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bookmark.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      if (selectedTags.length > 0) {
        filtered = filtered.filter(bookmark =>
          bookmark.tags?.some(tag => selectedTags.includes(tag))
        );
      }

      if (statusFilter !== 'all') {
        filtered = filtered.filter(bookmark => bookmark.status === statusFilter);
      }

      if (selectedCategories.length > 0) {
        filtered = filtered.filter(bookmark =>
          bookmark.categories?.some(cat => selectedCategories.includes(cat))
        );
      }

      if (readLaterOnly) {
        filtered = filtered.filter(bookmark => bookmark.readLater === true);
      }

      if (videoOnly) {
        filtered = filtered.filter(bookmark =>
          bookmark.mediaType === 'video' || isVideoUrl(bookmark.url)
        );
      }

      if (selectedCollections.length > 0) {
        const allowed = new Set();
        selectedCollections.forEach(id => {
          collectionBookmarkIdsRef.current.get(id)?.forEach(bid => allowed.add(bid));
        });
        filtered = filtered.filter(b => allowed.has(b.bookmarkId));
      }

      setFilteredBookmarks(filtered);
    } finally {
      setSearching(false);
    }
  }, [bookmarks, searchTerm, statusFilter, selectedTags, selectedCategories, readLaterOnly, videoOnly, selectedCollections]);

  useEffect(() => {
    filterBookmarks();
  }, [filterBookmarks]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      filterBookmarks();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedTags, statusFilter, videoOnly, filterBookmarks]);

  // ── Per-item handlers ─────────────────────────────────────────────────────

  const handleUpdateBookmark = async (bookmarkId, updateData) => {
    try {
      const updated = await bookmarkService.updateBookmark(bookmarkId, updateData);
      setBookmarks(prev => prev.map(b => b.bookmarkId === bookmarkId ? updated : b));
    } catch (error) {
      console.error('Error updating bookmark:', error);
      setError(error.message || 'Failed to update bookmark');
    }
  };

  const handleRetryBookmark = async (bookmarkId) => {
    try {
      await bookmarkService.retryBookmark(bookmarkId);
      setBookmarks(prev => prev.map(b =>
        b.bookmarkId === bookmarkId ? { ...b, status: 'processing' } : b
      ));
    } catch (error) {
      console.error('Error retrying bookmark:', error);
      setError(error.message || 'Failed to retry bookmark');
    }
  };

  const [retryingAll, setRetryingAll] = useState(false);
  const [retryAllProgress, setRetryAllProgress] = useState(null); // null | { done, total }

  const handleRetryAll = async () => {
    const processing = bookmarks.filter(b => b.status === 'processing');
    if (processing.length === 0) return;
    setRetryingAll(true);
    setRetryAllProgress({ done: 0, total: processing.length });
    let done = 0;
    for (const b of processing) {
      try {
        await bookmarkService.retryBookmark(b.bookmarkId);
      } catch (_) { /* continue even if one fails */ }
      done++;
      setRetryAllProgress({ done, total: processing.length });
      if (done < processing.length) await new Promise(r => setTimeout(r, 300));
    }
    setRetryingAll(false);
    setRetryAllProgress(null);
    loadBookmarks();
  };

  const [retryingAllFailed, setRetryingAllFailed] = useState(false);
  const [retryAllFailedProgress, setRetryAllFailedProgress] = useState(null);

  const handleRetryAllFailed = async () => {
    setRetryingAllFailed(true);
    // Fetch all failed bookmarks across all pages — the loaded bookmarks state
    // only contains the current page so its count is unreliable.
    const failed = [];
    let token = null;
    do {
      const response = await bookmarkService.getBookmarks({ limit: 100, status: 'failed', nextToken: token });
      failed.push(...(response.items || []));
      token = response.nextToken || null;
    } while (token);
    if (failed.length === 0) {
      setRetryingAllFailed(false);
      return;
    }
    setRetryAllFailedProgress({ done: 0, total: failed.length });
    let done = 0;
    for (const b of failed) {
      try {
        await bookmarkService.retryBookmark(b.bookmarkId);
        setBookmarks(prev => prev.map(x => x.bookmarkId === b.bookmarkId ? { ...x, status: 'processing' } : x));
      } catch (_) { /* continue even if one fails */ }
      done++;
      setRetryAllFailedProgress({ done, total: failed.length });
      if (done < failed.length) await new Promise(r => setTimeout(r, 300));
    }
    setRetryingAllFailed(false);
    setRetryAllFailedProgress(null);
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    try {
      await bookmarkService.deleteBookmark(bookmarkId);
      setBookmarks(prev => prev.filter(b => b.bookmarkId !== bookmarkId));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(bookmarkId); return next; });
      setError(null);
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      setError(error.message || 'Failed to delete bookmark');
    }
  };

  // ── Bulk selection handlers ───────────────────────────────────────────────

  const handleSelect = useCallback((bookmarkId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(bookmarkId)) next.delete(bookmarkId);
      else next.add(bookmarkId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredBookmarks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredBookmarks.map(b => b.bookmarkId)));
    }
  }, [selectedIds.size, filteredBookmarks]);

  const clearBulkState = () => {
    setBulkTagExpanded(false);
    setBulkTagInput('');
    setBulkDeleteConfirm(false);
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    const results = await Promise.allSettled(ids.map(id => bookmarkService.deleteBookmark(id)));
    const succeeded = new Set(ids.filter((_, i) => results[i].status === 'fulfilled'));
    const failCount = ids.length - succeeded.size;
    setBookmarks(prev => prev.filter(b => !succeeded.has(b.bookmarkId)));
    setSelectedIds(new Set());
    setBulkDeleteConfirm(false);
    if (failCount > 0) setError(`${succeeded.size} deleted, ${failCount} failed`);
  };

  const handleBulkTag = async (e) => {
    e.preventDefault();
    const tag = bulkTagInput.trim();
    if (!tag) return;
    const ids = [...selectedIds];
    const results = await Promise.allSettled(
      ids.map(id => {
        const bm = bookmarks.find(b => b.bookmarkId === id);
        const newTags = [...new Set([...(bm?.tags || []), tag])];
        return bookmarkService.updateBookmark(id, { tags: newTags }).then(updated => ({ id, updated }));
      })
    );
    const updates = results.filter(r => r.status === 'fulfilled').map(r => r.value);
    setBookmarks(prev => prev.map(b => {
      const hit = updates.find(u => u.id === b.bookmarkId);
      return hit ? hit.updated : b;
    }));
    setBulkTagInput('');
    setBulkTagExpanded(false);
    const count = updates.length;
    setBulkTagFeedback(`'${tag}' added to ${count} bookmark${count !== 1 ? 's' : ''}`);
    setTimeout(() => setBulkTagFeedback(null), 3000);
    // Intentionally keep selection — user may want to add another tag
  };

  // ── Filter/sidebar handlers ───────────────────────────────────────────────

  const handleCollectionSelect = async (collectionId) => {
    const isSelecting = !selectedCollections.includes(collectionId);
    if (isSelecting && !collectionBookmarkIdsRef.current.has(collectionId)) {
      setLoadingCollectionId(collectionId);
      try {
        const data = await bookmarkService.getCollection(collectionId);
        const ids = new Set((data.bookmarks || []).map(b => b.bookmarkId));
        collectionBookmarkIdsRef.current.set(collectionId, ids);
      } catch (e) {
        console.error('Failed to load collection bookmarks', e);
      } finally {
        setLoadingCollectionId(null);
      }
    }
    setSelectedCollections(prev =>
      prev.includes(collectionId) ? prev.filter(id => id !== collectionId) : [...prev, collectionId]
    );
  };

  const handleTagSelect = (tagName) => {
    setSelectedTags(prev =>
      prev.includes(tagName) ? prev.filter(t => t !== tagName) : [...prev, tagName]
    );
  };

  const handleCategorySelect = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSelectedTags([]);
    setSelectedCategories([]);
    setSelectedCollections([]);
    setReadLaterOnly(false);
    setVideoOnly(false);
  };

  const handleRefresh = () => {
    loadBookmarks({ silent: false, loadMore: false });
  };

  const handleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('bookmarkViewMode', mode);
  };

  const handleSortBy = (value) => {
    setSortBy(value);
    localStorage.setItem('bookmarkSortBy', value);
  };

  const sortedBookmarks = useMemo(() => {
    const sorted = [...filteredBookmarks];
    switch (sortBy) {
      case 'oldest':
        return sorted.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
      case 'title-asc':
        return sorted.sort((a, b) => (a.title || a.url || '').localeCompare(b.title || b.url || ''));
      case 'title-desc':
        return sorted.sort((a, b) => (b.title || b.url || '').localeCompare(a.title || a.url || ''));
      case 'newest':
      default:
        return sorted.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    }
  }, [filteredBookmarks, sortBy]);

  const activeFilterCount = (statusFilter !== 'all' ? 1 : 0) + selectedTags.length + selectedCategories.length + selectedCollections.length + (readLaterOnly ? 1 : 0) + (videoOnly ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0 || searchTerm.trim();

  // Virtual scrolling for list mode — only renders rows visible in the viewport.
  // scrollMargin = absolute distance from document top to the list container, so
  // the virtualizer knows which rows are in view when the page is scrolled.
  const listVirtualizer = useWindowVirtualizer({
    count: viewMode === 'list' ? sortedBookmarks.length : 0,
    estimateSize: () => 72,
    overscan: 10,
    scrollMargin: listContainerRef.current
      ? listContainerRef.current.getBoundingClientRect().top + window.scrollY
      : 0,
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const sidebarContent = (
    <div className="p-4 space-y-5">
      {/* Panel header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Filters</span>
        <button
          onClick={() => setIsFilterPanelOpen(false)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          aria-label="Close filters"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search bookmarks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-input pl-9 pr-8 text-sm w-full"
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-3.5 w-3.5 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {/* Reading list */}
      <button
        onClick={() => setReadLaterOnly(v => !v)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          readLaterOnly
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
      >
        <span>Reading List</span>
        {readLaterOnly && <span className="text-xs font-semibold">ON</span>}
      </button>

      {/* Videos */}
      <button
        onClick={() => setVideoOnly(v => !v)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          videoOnly
            ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
      >
        <span>Videos</span>
        {videoOnly && <span className="text-xs font-semibold">ON</span>}
      </button>

      {/* Status */}
      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
          Status
        </label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="form-input text-sm w-full"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="processed">Processed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Clear filters — above categories/tags so it's reachable without scrolling */}
      {hasActiveFilters && (
        <button
          onClick={handleClearFilters}
          className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
        >
          Clear all filters
        </button>
      )}

      {/* Collections */}
      {collections.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            Collections
          </p>
          <div className="space-y-0.5">
            {collections.map(col => {
              const isSelected = selectedCollections.includes(col.collectionId);
              const isLoading = loadingCollectionId === col.collectionId;
              return (
                <button
                  key={col.collectionId}
                  onClick={() => handleCollectionSelect(col.collectionId)}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-sm transition-colors text-left ${
                    isSelected
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {isLoading
                      ? <span className="h-3.5 w-3.5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      : <FolderIcon className={`h-3.5 w-3.5 flex-shrink-0 ${isSelected ? 'text-indigo-500' : 'text-gray-400'}`} />
                    }
                    <span className="truncate">{col.name}</span>
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{col.bookmarkCount}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Categories */}
      {availableCategories.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            Categories
          </p>
          <div className="flex flex-wrap gap-1.5">
            {availableCategories.map(cat => (
              <button
                key={cat}
                onClick={() => handleCategorySelect(cat)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedCategories.includes(cat)
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      <div>
        <TagsManager onTagSelect={handleTagSelect} selectedTags={selectedTags} />
      </div>
    </div>
  );

  return (
    <div className="flex gap-6 items-start relative">
      {/* Mobile backdrop */}
      {isFilterPanelOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 md:hidden"
          onClick={() => setIsFilterPanelOpen(false)}
        />
      )}

      {/* Filter panel — overlay drawer on mobile, in-flow panel on desktop */}
      {/* Mobile: fixed slide-in from left */}
      <aside
        className={`
          fixed top-0 left-0 bottom-0 z-30 w-72 bg-white dark:bg-gray-800 shadow-xl overflow-y-auto
          transform transition-transform duration-300 ease-in-out
          md:hidden
          ${isFilterPanelOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent}
      </aside>
      {/* Desktop: in-flow panel, shown/hidden without animation */}
      {isFilterPanelOpen && (
        <aside className="hidden md:block w-64 flex-shrink-0 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-6 max-h-[calc(100vh-5rem)] overflow-y-auto self-start">
          {sidebarContent}
        </aside>
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-4">
        {error && (
          <ErrorMessage message={error} onRetry={() => { setError(null); loadBookmarks(); }} />
        )}

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">My Bookmarks</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {filteredBookmarks.length} of {bookmarks.length}{nextToken ? '+' : ''} bookmarks
            </p>
            {bookmarks.some(b => b.status === 'processing') && (
              <button
                onClick={handleRetryAll}
                disabled={retryingAll}
                className="mt-2 flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-1.5 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon className={`h-4 w-4 ${retryingAll ? 'animate-spin' : ''}`} />
                {retryingAll && retryAllProgress
                  ? `Retrying… ${retryAllProgress.done}/${retryAllProgress.total}`
                  : `Retry all processing (${bookmarks.filter(b => b.status === 'processing').length})`}
              </button>
            )}
            {bookmarks.some(b => b.status === 'failed') && (
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={handleRetryAllFailed}
                  disabled={retryingAllFailed}
                  className="flex items-center gap-1.5 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-1.5 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${retryingAllFailed ? 'animate-spin' : ''}`} />
                  {retryingAllFailed && retryAllFailedProgress
                    ? `Retrying… ${retryAllFailedProgress.done}/${retryAllFailedProgress.total}`
                    : 'Retry all failed'}
                </button>
                <Link to="/broken-links" className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors">
                  Manage →
                </Link>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {/* Filter toggle button */}
            <button
              onClick={() => setIsFilterPanelOpen(v => !v)}
              className={`relative p-2 rounded-md transition-colors ${
                isFilterPanelOpen
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-label={isFilterPanelOpen ? 'Close filters' : 'Open filters'}
              title="Filters"
            >
              <FunnelIcon className="h-5 w-5" />
              {!isFilterPanelOpen && activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Sort */}
            <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1">
              <BarsArrowDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <select
                value={sortBy}
                onChange={e => handleSortBy(e.target.value)}
                className="text-sm text-gray-600 dark:text-gray-300 bg-transparent dark:bg-transparent focus:outline-none cursor-pointer"
                title="Sort order"
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="title-asc">Title A–Z</option>
                <option value="title-desc">Title Z–A</option>
              </select>
            </div>

            {/* View toggle */}
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
              <button
                onClick={() => handleViewMode('grid')}
                title="Grid view"
                className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleViewMode('list')}
                title="List view"
                className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                <ListBulletIcon className="h-4 w-4" />
              </button>
            </div>

            <Link
              to="/import-export"
              title="Import / Export"
              className="flex items-center gap-1.5 p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <ArrowsUpDownIcon className="h-4 w-4" />
              <span className="hidden sm:inline text-sm">Import / Export</span>
            </Link>

            <button
              onClick={handleRefresh}
              disabled={loading || refreshing}
              title="Refresh"
              className="flex items-center gap-1.5 p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline text-sm">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* ── Active collection filter chips ────────────────────────────── */}
        {selectedCollections.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {selectedCollections.map(colId => {
              const col = collections.find(c => c.collectionId === colId);
              if (!col) return null;
              return (
                <span
                  key={colId}
                  className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 text-indigo-800 dark:text-indigo-300 text-sm rounded-full"
                >
                  <FolderIcon className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                  {col.name}
                  <button
                    onClick={() => handleCollectionSelect(colId)}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
                    aria-label={`Remove ${col.name} filter`}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
            {selectedCollections.length > 1 && (
              <button
                onClick={() => setSelectedCollections([])}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        )}

        {/* ── Bulk action bar ────────────────────────────────────────────── */}
        {selectionActive && (
          <div className="flex flex-wrap items-center justify-between gap-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl px-4 py-2.5">
            {/* Left: count + select-all toggle */}
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm font-medium text-primary-900 dark:text-primary-200 whitespace-nowrap">
                {selectedIds.size} selected
              </span>
              <button
                onClick={handleSelectAll}
                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 whitespace-nowrap transition-colors"
              >
                {selectedIds.size === filteredBookmarks.length
                  ? 'Deselect all'
                  : `Select all ${filteredBookmarks.length}`}
              </button>
              <button
                onClick={() => { setSelectedIds(new Set()); clearBulkState(); }}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Clear
              </button>
            </div>

            {/* Right: actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Tag feedback */}
              {bulkTagFeedback && (
                <span className="text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-2.5 py-1 rounded-lg">
                  {bulkTagFeedback}
                </span>
              )}

              {/* Tag — inline input or trigger button */}
              {bulkTagExpanded ? (
                <form onSubmit={handleBulkTag} className="flex items-center gap-1.5">
                  <input
                    autoFocus
                    type="text"
                    value={bulkTagInput}
                    onChange={e => setBulkTagInput(e.target.value)}
                    list="bulk-tag-suggestions"
                    placeholder="Tag name"
                    className="border border-primary-300 dark:border-primary-600 rounded-lg px-2.5 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1 min-w-0 max-w-[8rem]"
                  />
                  <datalist id="bulk-tag-suggestions">
                    {allTags.map(t => <option key={t} value={t} />)}
                  </datalist>
                  <button
                    type="submit"
                    disabled={!bulkTagInput.trim()}
                    className="text-xs bg-primary-600 text-white px-2.5 py-1 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => { setBulkTagExpanded(false); setBulkTagInput(''); }}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </form>
              ) : (
                !bulkDeleteConfirm && (
                  <button
                    onClick={() => setBulkTagExpanded(true)}
                    className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-400 px-2.5 py-1 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                  >
                    <TagIcon className="h-4 w-4" />
                    Tag
                  </button>
                )
              )}

              {/* Add to collection */}
              {!bulkDeleteConfirm && !bulkTagExpanded && (
                <button
                  onClick={() => setBulkCollectionOpen(true)}
                  className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-400 px-2.5 py-1 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                >
                  <FolderPlusIcon className="h-4 w-4" />
                  Collect
                </button>
              )}

              {/* Delete — inline confirm */}
              {bulkDeleteConfirm ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-700 dark:text-red-400 whitespace-nowrap">
                    Delete {selectedIds.size} bookmark{selectedIds.size !== 1 ? 's' : ''}?
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    className="text-xs bg-red-600 text-white px-2.5 py-1 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setBulkDeleteConfirm(false)}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                !bulkTagExpanded && (
                  <button
                    onClick={() => setBulkDeleteConfirm(true)}
                    className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 px-2.5 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </button>
                )
              )}
            </div>
          </div>
        )}

        {/* Bookmarks */}
        {sortedBookmarks.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {searchTerm || statusFilter !== 'all' ? 'No matching bookmarks' : 'No bookmarks found'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Start by adding your first bookmark.'
              }
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {sortedBookmarks.map((bookmark) => (
              <BookmarkCard
                key={bookmark.bookmarkId}
                bookmark={bookmark}
                onDelete={handleDeleteBookmark}
                onUpdate={handleUpdateBookmark}
                onRetry={handleRetryBookmark}
                highlighted={bookmark.bookmarkId === highlightId}
                selected={selectedIds.has(bookmark.bookmarkId)}
                selectionActive={selectionActive}
                onSelect={handleSelect}
              />
            ))}
          </div>
        ) : (
          <div ref={listContainerRef} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div style={{ height: `${listVirtualizer.getTotalSize()}px`, position: 'relative' }}>
              {listVirtualizer.getVirtualItems().map(vItem => (
                <div
                  key={vItem.key}
                  data-index={vItem.index}
                  ref={listVirtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vItem.start - listVirtualizer.options.scrollMargin}px)`,
                  }}
                >
                  <BookmarkListRow
                    bookmark={sortedBookmarks[vItem.index]}
                    onDelete={handleDeleteBookmark}
                    onUpdate={handleUpdateBookmark}
                    onRetry={handleRetryBookmark}
                    highlighted={sortedBookmarks[vItem.index]?.bookmarkId === highlightId}
                    selected={selectedIds.has(sortedBookmarks[vItem.index]?.bookmarkId)}
                    selectionActive={selectionActive}
                    onSelect={handleSelect}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Load more */}
        {nextToken && (
          <div className="flex justify-center pt-2">
            <button
              onClick={() => loadBookmarks({ loadMore: true, token: nextToken })}
              disabled={loadingMore}
              className="px-5 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {loadingMore
                ? <span className="flex items-center gap-2"><ArrowPathIcon className="h-4 w-4 animate-spin" />Loading…</span>
                : 'Load more bookmarks'}
            </button>
          </div>
        )}
      </div>

      {/* Bulk add-to-collection modal */}
      <AddToCollectionModal
        bookmarkIds={[...selectedIds]}
        isOpen={bulkCollectionOpen}
        onClose={() => {
          setBulkCollectionOpen(false);
          setSelectedIds(new Set());
          clearBulkState();
        }}
      />
    </div>
  );
};

export default BookmarkList;
